import { useSuspenseQuery } from '@apollo/client';
import { useMutation } from '@apollo/client';
import React, { useMemo } from 'react';

import {
  eluveAdminHasuraContext,
  useCompleteFragment,
} from '@eluve/apollo-client';
import { Stack, Switch, Text, useToast } from '@eluve/components';
import { ColDefBuilder, DataTable, Heading } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTenantIdFromParams } from '@eluve/session-helpers';

import {
  BundleBlockerTooltip,
  type TenantBundleBlocker,
} from './components/BundleBlockerTooltip';
import {
  getTenantEnabledBundlesForDepsQuery,
  groupBlockersById,
} from './components/tenant-template-deps.operations';

const llmOutputTemplateFragment = graphql(`
  fragment LlmOutputTemplate on LlmOutputTemplates @_unmask {
    __typename
    id
    name
    description
    llmOutputType
    updatedAt
  }
`);

const tenantLlmOutputTemplateFragment = graphql(
  `
    fragment TenantLlmOutputTemplate on TenantLlmOutputTemplates @_unmask {
      __typename
      tenantId
      llmOutputTemplateId
      isEnabled
      updatedAt
      llm_output_template {
        ...LlmOutputTemplate
      }
    }
  `,
  [llmOutputTemplateFragment],
);

const tenantLlmOutputTemplatesFragment = graphql(
  `
    fragment TenantLlmOutputTemplates on Tenants @_unmask {
      id
      __typename
      llmOutputTemplates(
        where: { llm_output_template: { isDraft: { _eq: false } } }
      ) {
        llmOutputTemplateId
        tenantId
        __typename
        updatedAt
        isEnabled
        llm_output_template {
          ...LlmOutputTemplate
        }
      }
    }
  `,
  [tenantLlmOutputTemplateFragment, llmOutputTemplateFragment],
);

const getLlmOutputTemplatesQuery = graphql(
  `
    query getLlmOutputTemplates($tenantId: uuid!) {
      tenantsByPk(id: $tenantId) {
        __typename
        id
        ...TenantLlmOutputTemplates
      }
    }
  `,
  [tenantLlmOutputTemplatesFragment],
);

const setTenantLlmOutputTemplateMutation = graphql(
  `
    mutation setTenantLlmOutputTemplate(
      $tenantId: uuid!
      $llmOutputTemplateId: uuid!
      $isEnabled: Boolean!
    ) {
      insertTenantLlmOutputTemplatesOne(
        object: {
          tenantId: $tenantId
          llmOutputTemplateId: $llmOutputTemplateId
          isEnabled: $isEnabled
        }
        onConflict: {
          constraint: tenant_llm_output_templates_pkey
          updateColumns: [isEnabled]
        }
      ) {
        ...TenantLlmOutputTemplate
      }
    }
  `,
  [tenantLlmOutputTemplateFragment],
);

type OutputTemplateRow = {
  isEnabled: boolean;
  id: string;
  name: string;
  description: string;
  outputType: string;
  tenantId: string;
  templateUpdatedAt: string;
  accessUpdatedAt: string;
  blockedBy: TenantBundleBlocker[];
};

const TenantLlmOutputTemplateToggle: React.FC<{
  tenantId: string;
  name: string;
  llmOutputTemplateId: string;
  blockedBy?: TenantBundleBlocker[];
}> = ({ tenantId, name, llmOutputTemplateId, blockedBy }) => {
  const toast = useToast();
  const data = useCompleteFragment({
    fragment: tenantLlmOutputTemplateFragment,
    key: {
      llmOutputTemplateId,
      tenantId,
    },
    strict: false,
  });

  const [updateTenantLlmOutputTemplate] = useMutation(
    setTenantLlmOutputTemplateMutation,
    {
      onError: () =>
        toast.error(`Failed to update access to output template ${name}`),
      optimisticResponse: (data) => ({
        insertTenantLlmOutputTemplatesOne: {
          __typename: 'TenantLlmOutputTemplates' as const,
          updatedAt: new Date().toISOString(),
          ...data,
          llm_output_template: {
            __typename: 'LlmOutputTemplates' as const,
            id: llmOutputTemplateId,
            name,
            description: '',
            updatedAt: '',
            llmOutputType: 'SOAP_NOTE' as const,
          },
        },
      }),
    },
  );
  const submit = async (isEnabled: boolean) => {
    await updateTenantLlmOutputTemplate({
      context: eluveAdminHasuraContext,
      variables: {
        tenantId,
        llmOutputTemplateId,
        isEnabled,
      },
    });
  };

  const isBlocked = blockedBy !== undefined && blockedBy.length > 0;

  const switchEl = (
    <Switch
      size="sm"
      disabled={isBlocked}
      onCheckedChange={(isChecked) => submit(isChecked)}
      checked={data?.isEnabled ?? false}
    />
  );

  if (!isBlocked) {
    return switchEl;
  }

  return (
    <BundleBlockerTooltip blockedBy={blockedBy}>
      {switchEl}
    </BundleBlockerTooltip>
  );
};

const tenantOutputTemplateColumns = new ColDefBuilder<OutputTemplateRow>()
  .linkSortable('name', (row) => `/admin/output-templates/${row.id}`)
  .defaultSortable('description', {
    cellRenderer: (row) =>
      row.description ? (
        <div className="max-w-md truncate" title={row.description}>
          {row.description}
        </div>
      ) : (
        <Text color="disabled">—</Text>
      ),
  })
  .defaultSortable('outputType', 'Output Type')
  .defaultSortable('isEnabled', {
    label: 'Is Enabled',
    cellRenderer: (row) => {
      return (
        <TenantLlmOutputTemplateToggle
          tenantId={row.tenantId}
          llmOutputTemplateId={row.id}
          name={row.name}
          blockedBy={row.blockedBy}
        />
      );
    },
  })
  .dateSortable('templateUpdatedAt', 'Template Updated At')
  .dateSortable('accessUpdatedAt', 'Access Updated At')
  .build();

const TenantLlmOutputTemplates: React.FC<{
  blockersByOutputTemplateId: Map<string, TenantBundleBlocker[]>;
}> = ({ blockersByOutputTemplateId }) => {
  const tenantId = useTenantIdFromParams()!;
  const data = useCompleteFragment({
    fragment: tenantLlmOutputTemplatesFragment,
    key: {
      id: tenantId,
    },
  });
  const rows = useMemo(() => {
    return (data?.llmOutputTemplates ?? []).map<OutputTemplateRow>(
      (llmOutputTemplate) => ({
        id: llmOutputTemplate.llm_output_template.id,
        name: llmOutputTemplate.llm_output_template.name,
        tenantId,
        description: llmOutputTemplate.llm_output_template.description ?? '',
        outputType: llmOutputTemplate.llm_output_template.llmOutputType,
        isEnabled: llmOutputTemplate.isEnabled,
        accessUpdatedAt: llmOutputTemplate.updatedAt,
        templateUpdatedAt: llmOutputTemplate.llm_output_template.updatedAt,
        blockedBy:
          blockersByOutputTemplateId.get(
            llmOutputTemplate.llm_output_template.id,
          ) ?? [],
      }),
    );
  }, [data, tenantId, blockersByOutputTemplateId]);

  return (
    <Stack className="grid" gap={2}>
      <Stack gap={2}>
        <Heading size="2xl" weight="semibold">
          LLM Output Templates
        </Heading>
        <Text>
          Control which LLM output templates this organization will have access
          to
        </Text>
      </Stack>
      <DataTable
        data={rows}
        columns={tenantOutputTemplateColumns}
        isPaginated={false}
        enableGlobalSearch
        initialSortingState={[
          {
            id: 'templateUpdatedAt',
            desc: true,
          },
        ]}
      />
    </Stack>
  );
};

export const AdminTenantLlmOutputTemplatesPage: React.FC = () => {
  const tenantId = useTenantIdFromParams()!;

  useSuspenseQuery(getLlmOutputTemplatesQuery, {
    variables: {
      tenantId,
    },
    context: eluveAdminHasuraContext,
  });

  const {
    data: { tenantTemplateBundles: enabledBundles },
  } = useSuspenseQuery(getTenantEnabledBundlesForDepsQuery, {
    variables: {
      tenantId,
    },
  });

  const blockersByOutputTemplateId = useMemo(
    () =>
      groupBlockersById(
        enabledBundles,
        (ttb) => ttb.llm_model_prompt_template.outputTemplateId,
      ),
    [enabledBundles],
  );

  return (
    <Stack gap={2}>
      <TenantLlmOutputTemplates
        blockersByOutputTemplateId={blockersByOutputTemplateId}
      />
    </Stack>
  );
};
